/** Labour demand alignment table for the Labour Management demo screen - compares labour
 * hours against operational demand (forecast covers/guests) so the page reads as a
 * strategic foodservice labour tool, not just a rota screen. */

import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DemandAlignmentRow } from '../types';
import { StatusChip } from './StatusChip';

interface DemandAlignmentTableProps {
  rows: DemandAlignmentRow[];
  currencyCode?: string;
  onSelectSite: (site: string) => void;
}

const STATUS_LABEL = { good: 'On track', warning: 'Tight', critical: 'At risk' };

const DemandAlignmentTable = ({ rows, currencyCode, onSelectSite }: DemandAlignmentTableProps) => {
  const { t } = useTranslation('common');
  const currencyPrefix = currencyCode === 'USD' ? '$' : currencyCode === 'EUR' ? '\u20ac' : '\u00a3';

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('labourManagementSite', 'Site')}</TableCell>
            <TableCell>{t('labourManagementServicePeriod', 'Service period')}</TableCell>
            <TableCell>{t('labourManagementForecast', 'Forecast')}</TableCell>
            <TableCell align="right">{t('labourManagementRequiredHours', 'Required hours')}</TableCell>
            <TableCell align="right">{t('labourManagementScheduledHours', 'Scheduled hours')}</TableCell>
            <TableCell align="right">{t('labourManagementVariance', 'Variance')}</TableCell>
            <TableCell align="right">{t('labourManagementLabourCostPerCover', 'Labour cost per cover')}</TableCell>
            <TableCell>{t('labourManagementStatusLabel', 'Status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={8}>{t('labourManagementNoResults', 'No sites match the current filters.')}</TableCell></TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <Link component="button"
                  onClick={() => onSelectSite(row.site)}
                  underline="hover">
                  {row.site}
                </Link>
              </TableCell>
              <TableCell>{row.servicePeriod}</TableCell>
              <TableCell>{row.forecastLabel}</TableCell>
              <TableCell align="right">{row.requiredHours}</TableCell>
              <TableCell align="right">{row.scheduledHours}</TableCell>
              <TableCell align="right">{row.variance > 0 ? `+${row.variance}` : row.variance}</TableCell>
              <TableCell align="right">{`${currencyPrefix}${row.labourCostPerCover.toFixed(2)}`}</TableCell>
              <TableCell>
                <StatusChip status={row.status}
                  label={t(`labourManagementDemandStatus${STATUS_LABEL[row.status].replace(' ', '')}`, STATUS_LABEL[row.status])} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DemandAlignmentTable;
